/**
 * B338 — what the buyer's cart says while it is working, and which door
 * it shuts while it says it.
 *
 * The storefront mints five async acts from `App.tsx`. Three already had
 * an honest face when this was written — `addToCart` → `adding` →
 * `<Busy label="Adding to your cart…" />`, `checkout` → the
 * CHECKOUT_IN_FLIGHT screen, `pay` → the Pay button's own `busy` — and
 * two did not: `removeLine` and `applyPromo` had no in-flight state of
 * any kind, no flag, no face, no announcement, while both fired real
 * round-trips (`cart.remove` PLUS a `cart.get`, and `cart.apply_promo`).
 *
 * The silence was not the worst of it. `screens.tsx`'s Remove was a bare
 * ghost `<Button>` with no `busy` and no `disabled`, and `removeLine` had
 * no re-entrancy guard — so a buyer who pressed Remove, saw the line stay
 * put and heard nothing pressed again, which is not misuse: it is the only
 * information the screen gave them. The first call succeeded. The second
 * reached `removeCartLine`, which throws `CartLineNotFoundError` on a line
 * that is already gone, and the cart painted a refusal notice FOR AN
 * ACTION THAT HAD WORKED. That is strictly worse than silence — silence
 * withholds information, this manufactures a false one. `cartRemoveIsNotIdempotent`
 * in `cartBusyFace.test.tsx` drives it against the real domain function.
 *
 * Keyed on the ACT, deliberately, and not on the notice prose the same
 * container carries — B278's stance, kept for the fourth app: a screen
 * keying on prose breaks the next time someone improves the wording of a
 * refusal. The refusal's words belong to `refusals.ts`; the working
 * sentence belongs here.
 *
 * ONE field, not one flag per door (acceptance (c)/(e)). The field holds
 * the KEY of the act in the air — posData's own shape,
 * `cart.remove:${cartId}:${lineId}` — so it is single-valued by
 * construction: exactly one door can match it, which is B320's
 * one-door-per-act rule holding by arithmetic rather than by discipline,
 * and one live region is therefore the correct number rather than a
 * compromise. The act is READ BACK off the key's prefix rather than
 * stored beside it, because two fields that must agree are two fields
 * that can disagree.
 *
 * The promo door is cured with Remove and the reason is NOT the same.
 * `applyPromoCode` is idempotent by construction (`if
 * (cart.enteredCodes.includes(code)) return cart;`), so a second press is
 * harmless on the wire; its defect is announcement-and-door only. The two
 * are one screen's one silence, not one risk, and this file says so
 * rather than letting the stronger finding lend the weaker one its
 * urgency.
 *
 * An act this file has not been told about degrades to plain words rather
 * than shouting a wire id (R7 applies to a `role="status"` exactly as it
 * does to a heading). That fallback is not a license to add an act
 * mutely: `packages/ui/test/fleet-busy-wall.test.ts` derives the fleet's
 * population of apps-that-mint-acts from source and reddens when one owes
 * a vocabulary and has none.
 */

/** The cart acts the buyer can start from the cart screen. */
export type CartAct = "cart.remove" | "cart.set_quantity" | "cart.apply_promo";

/** Present continuous, buyer words, one per act. */
const BUSY_WORDS: Readonly<Record<CartAct, string>> = {
  /* The buyer's noun for the thing, not the wire's: they are looking at a
     cart with a line in it, and "that" is the row their finger is on. */
  "cart.remove": "Taking that out of your cart…",
  /* B391 — "how many" in the buyer's words. Not "Setting the quantity":
     the buyer changed their mind about how many tickets they want, and
     the wire's noun for that is not theirs. */
  "cart.set_quantity": "Changing how many…",
  /* Deliberately not "Applying" — the seller may say no, and a sentence
     that promises the outcome before the answer arrives is the same defect
     B312 cured one field over. Checking is what is actually happening. */
  "cart.apply_promo": "Checking your code…",
};

/**
 * The sentence for an act this file has not been told about. It says
 * something true and nothing specific, which is the honest answer when the
 * cart cannot name its own act — never a wire id, on the one surface in
 * the fleet a stranger spending money is looking at.
 */
export const BUSY_FALLBACK = "Working on it…";

/** The acts the cart can name. Exported so a wall can read the set. */
export function namedCartActs(): readonly string[] {
  return Object.keys(BUSY_WORDS);
}

/**
 * The key for an act in flight — `apps/pos/src/lib/posData.ts`'s shape,
 * which is the point: the till has run this exact verb with this exact key
 * shape since B334, and the buyer's cart differing from it by so much as a
 * separator would be two answers to one question.
 *
 * `undefined` when there is no cart yet: an act cannot be in flight on a
 * cart that does not exist, and a key minted from a missing id would be a
 * string that silently matches nothing.
 */
export function cartActKey(act: CartAct, cartId: string | undefined, lineId?: string): string | undefined {
  if (cartId === undefined) return undefined;
  return lineId === undefined ? `${act}:${cartId}` : `${act}:${cartId}:${lineId}`;
}

/** The act a key is about, read back off its own prefix. */
export function actOf(key: string | undefined): CartAct | undefined {
  if (key === undefined) return undefined;
  for (const act of Object.keys(BUSY_WORDS) as CartAct[]) {
    if (key === act || key.startsWith(`${act}:`)) return act;
  }
  return undefined;
}

/**
 * What the buyer is told while the cart works.
 * `undefined` when nothing is in the air — silence is the right face for a
 * cart at rest, and a live region holding stale words is worse than an
 * empty one.
 */
export function busyWords(busy: string | undefined): string | undefined {
  if (busy === undefined) return undefined;
  const act = actOf(busy);
  return act === undefined ? BUSY_FALLBACK : BUSY_WORDS[act];
}

/**
 * Whether THIS door is the one in flight. `false` for every other door on
 * the screen, which is what keeps one act from lighting the whole cart.
 */
export function doorBusy(busy: string | undefined, key: string | undefined): boolean {
  return busy !== undefined && key !== undefined && busy === key;
}

/**
 * The re-entrancy guard, pure and single-valued: a cart with an act in the
 * air starts no second act.
 *
 * The shut door (acceptance (d)) is the cure a buyer can see; this is the
 * one that holds when the press arrives from somewhere a `disabled`
 * attribute does not reach — a keyboard repeat landing in the same tick, a
 * synthetic event, a future caller. Belt and braces, and the braces are
 * cheap.
 */
export function admitsCartAct(current: string | undefined): boolean {
  return current === undefined;
}
